import { FC, useState } from "react";

import { translate } from "@/utils/formatters";
import {
  isValidEmailFormat,
  isEmailDomainAllowed,
} from "@/utils/emailValidation";
import { sendReceiptPdfEmail } from "@/api/extendedServices";
import { getEmailExportConfig } from "@/config/emailExportWidgets";
import { Send, Spinner } from "@/components/icons";

import styles from "./EmailExportSection.module.css";

interface EmailExportSectionProps {
  userId: string;
  receiptId: string;
  lang: string;
  settingsId: string;
  storeName?: string | null;
  initialEmail?: string | null;
  onSendSuccess?: (email: string) => void;
}

export const EmailExportSection: FC<EmailExportSectionProps> = ({
  userId,
  receiptId,
  lang,
  settingsId,
  storeName,
  initialEmail,
  onSendSuccess,
}) => {
  const [email, setEmail] = useState(initialEmail ?? "");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const emailConfig = getEmailExportConfig(settingsId);

  if (!emailConfig) return null;

  const whitelist = emailConfig.domainWhitelist;
  const singleDomain = whitelist?.length === 1 ? whitelist[0] : null;

  const handleSend = async () => {
    setError(null);
    setSuccess(false);

    if (!isValidEmailFormat(email)) {
      setError(
        translate(lang, "Ugyldig e-postadresse", "Invalid email address")
      );
      return;
    }

    if (!isEmailDomainAllowed(email, whitelist ?? null)) {
      setError(
        singleDomain
          ? translate(
              lang,
              `Må være en @${singleDomain}-adresse`,
              `Must be an @${singleDomain} address`
            )
          : translate(
              lang,
              "E-postdomenet er ikke tillatt",
              "Email domain is not allowed"
            )
      );
      return;
    }

    setIsLoading(true);
    try {
      await sendReceiptPdfEmail(userId, receiptId, {
        email,
        lang,
        storeName: storeName ?? null,
      });
      setSuccess(true);
      onSendSuccess?.(email);
    } catch {
      setError(
        translate(
          lang,
          "Kunne ikke sende e-post. Prøv igjen.",
          "Failed to send email. Please try again."
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fieldClass = `${styles.field} ${error ? styles.fieldError : ""} ${
    success ? styles.fieldSuccess : ""
  }`;

  const placeholderDomain = singleDomain ? `navn@${singleDomain}` : "";

  const renderHelperMessage = () => {
    if (error) {
      return (
        <p className={`${styles.helperText} ${styles.errorText}`}>
          <span
            className={`${styles.statusDot} ${styles.statusDotError}`}
            aria-hidden="true"
          />
          {error}
        </p>
      );
    }
    if (success) {
      return (
        <p className={`${styles.helperText} ${styles.successText}`}>
          <span
            className={`${styles.statusDot} ${styles.statusDotSuccess}`}
            aria-hidden="true"
          />
          {translate(lang, "E-post sendt", "Email sent")}
        </p>
      );
    }
    if (singleDomain) {
      return (
        <p className={styles.helperText}>
          {translate(lang, "Kun", "Only")}
          <strong>@{singleDomain}</strong>
        </p>
      );
    }
    return null;
  };

  return (
    <div className={styles.container}>
      <div className={fieldClass}>
        <input
          id={`email-export-${settingsId}`}
          type="email"
          inputMode="email"
          autoComplete="email"
          className={styles.input}
          placeholder={
            placeholderDomain ||
            translate(lang, "E-postadresse", "Email address")
          }
          value={email}
          disabled={isLoading}
          aria-invalid={!!error}
          aria-describedby={`email-export-msg-${settingsId}`}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
            setSuccess(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !isLoading) handleSend();
          }}
        />
        <button
          type="button"
          className={styles.submitButton}
          onClick={handleSend}
          disabled={isLoading || email.length === 0}
        >
          {isLoading ? (
            <Spinner size={14} />
          ) : (
            <>
              <span className={styles.submitIcon}>
                <Send size={14} color="currentColor" />
              </span>
              {translate(lang, "Send", "Send")}
            </>
          )}
        </button>
      </div>

      <div
        id={`email-export-msg-${settingsId}`}
        className={styles.helperRow}
        role={error ? "alert" : "status"}
        aria-live="polite"
      >
        {renderHelperMessage()}
      </div>
    </div>
  );
};
